import {
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";
import {
  CakeOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  LocationOnOutlined,
  PersonOutline,
  PhoneOutlined,
  WhatsApp,
} from "@mui/icons-material";

interface InfoItem {
  label: string;
  value: ReactNode;
  icon?: "phone" | "birth" | "address" | "social";
}

interface DemographicRow {
  label: string;
  display: string;
}

interface VitalItem {
  name: string;
  value?: string | number;
  unit?: string;
}

interface EhrPatientHeaderMobileProps {
  loading?: boolean;
  displayName: string;
  initials: string;
  metaText?: string;
  canEditProfile?: boolean;
  onEditProfile?: () => void;
  infoItems: InfoItem[];
  vitals?: VitalItem[];
  demographics?: DemographicRow[];
  showDemographics?: boolean;
  onToggleDemographics?: () => void;
}

const INFO_ICONS = {
  phone: PhoneOutlined,
  birth: CakeOutlined,
  address: LocationOnOutlined,
  social: PersonOutline,
} as const;

function InfoRow({ label, value, icon }: InfoItem) {
  const Icon = icon ? INFO_ICONS[icon] : null;

  return (
    <Stack direction="row" spacing={1.1} alignItems="flex-start">
      {Icon ? (
        <Box
          sx={{
            mt: 0.15,
            width: 28,
            height: 28,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "action.hover",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16, color: "text.secondary" }} />
        </Box>
      ) : null}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ display: "block", mb: 0.25, lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <Box sx={{ minWidth: 0 }}>{value}</Box>
      </Box>
    </Stack>
  );
}

/** Cabeçalho do paciente no sumário mobile — card único, alinhado ao restante do EHR. */
export function EhrPatientHeaderMobile({
  loading,
  displayName,
  initials,
  metaText,
  canEditProfile,
  onEditProfile,
  infoItems,
  vitals,
  demographics,
  showDemographics,
  onToggleDemographics,
}: EhrPatientHeaderMobileProps) {
  const theme = useTheme();

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          borderLeftWidth: 4,
          borderLeftColor: "primary.main",
          bgcolor: "background.paper",
          p: 1.35,
          boxShadow: "0 2px 12px rgba(15, 23, 42, 0.05)",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Skeleton variant="circular" width={56} height={56} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={28} />
            <Skeleton variant="text" width="40%" height={18} />
          </Box>
        </Stack>
        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
          <Skeleton variant="rounded" height={44} />
          <Skeleton variant="rounded" height={44} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderLeftWidth: 4,
        borderLeftColor: "primary.main",
        bgcolor: "background.paper",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Box sx={{ px: 1.35, pt: 1.35, pb: canEditProfile ? 1 : 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              fontWeight: 800,
              fontSize: "1.05rem",
              bgcolor: "primary.main",
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.22)}`,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{
                fontSize: "1.05rem",
                lineHeight: 1.25,
                letterSpacing: "-0.02em",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {displayName || "—"}
            </Typography>

            {metaText ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: "block", mt: 0.4, lineHeight: 1.3 }}
              >
                {metaText}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {canEditProfile ? (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
            onClick={onEditProfile}
            sx={{
              mt: 1.1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              minHeight: 38,
              borderColor: "divider",
              color: "text.primary",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Editar perfil
          </Button>
        ) : null}
      </Box>

      {infoItems.length > 0 ? (
        <>
          <Divider />
          <Stack spacing={1.35} sx={{ px: 1.35, py: 1.25 }}>
            {infoItems.map((item) => (
              <InfoRow key={item.label} {...item} />
            ))}
          </Stack>
        </>
      ) : null}

      {vitals && vitals.length > 0 ? (
        <>
          <Divider />
          <Box sx={{ px: 1.35, py: 1.15 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ display: "block", mb: 0.85 }}
            >
              Últimas medições
            </Typography>
            <Stack
              direction="row"
              spacing={0.85}
              sx={{
                overflowX: "auto",
                pb: 0.25,
                mx: -0.25,
                px: 0.25,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {vitals.map((observation) => (
                <Box
                  key={`${observation.name}-${observation.value}`}
                  sx={{
                    flex: "0 0 auto",
                    minWidth: 96,
                    px: 1,
                    py: 0.85,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{
                      display: "block",
                      mb: 0.25,
                      fontSize: "0.625rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {observation.name}
                  </Typography>
                  <Typography variant="body2" fontWeight={800} lineHeight={1.2}>
                    {`${observation.value ?? "—"} ${observation.unit ?? ""}`.trim()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </>
      ) : null}

      {demographics && demographics.length > 0 && onToggleDemographics ? (
        <>
          <Divider />
          <Box sx={{ px: 0.75, py: 0.35 }}>
            <Button
              fullWidth
              size="small"
              variant="text"
              onClick={onToggleDemographics}
              endIcon={showDemographics ? <ExpandLess /> : <ExpandMore />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "text.secondary",
                justifyContent: "space-between",
                px: 0.75,
                minHeight: 40,
              }}
            >
              {showDemographics
                ? "Ocultar dados demográficos"
                : "Ver dados demográficos"}
            </Button>

            <Collapse in={showDemographics}>
              <Stack spacing={1.1} sx={{ px: 0.6, pb: 1.1 }}>
                {demographics.map((row) => (
                  <Stack key={row.label} spacing={0.2}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                    >
                      {row.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {row.display}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Collapse>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

/** Valor de telefone com atalho WhatsApp para profissionais. */
export function EhrPatientPhoneValue({
  phone,
  formattedPhone,
  showWhatsApp,
  onWhatsApp,
}: {
  phone?: string;
  formattedPhone: string;
  showWhatsApp?: boolean;
  onWhatsApp?: () => void;
}) {
  if (!phone) {
    return (
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
      <Typography variant="body2" fontWeight={700}>
        {formattedPhone}
      </Typography>
      {showWhatsApp ? (
        <Tooltip title={`WhatsApp ${formattedPhone}`}>
          <IconButton
            size="small"
            onClick={onWhatsApp}
            sx={{
              width: 30,
              height: 30,
              bgcolor: alpha("#25D366", 0.12),
              color: "#128C7E",
              "&:hover": { bgcolor: alpha("#25D366", 0.2) },
            }}
          >
            <WhatsApp sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}
